/* eslint-disable */
import type { BrowserInfo, SupportInfo } from '../core/types'

/**
 * Parses the User-Agent string to determine browser name and version.
 *
 * Detection order matters: more specific browsers (Edge, Opera, Brave,
 * Samsung) must be checked before Chrome because they include "Chrome"
 * in their UA string.
 */
export function detectBrowser (): BrowserInfo {
  const ua = navigator.userAgent
  let name: BrowserInfo['name'] = 'unknown'
  let version = ''

  if ((navigator as any).brave && typeof (navigator as any).brave.isBrave === 'function') {
    name = 'brave'
    version = extractVersion(ua, /Chrome\/(\d+[\d.]*)/)
  } else if (/SamsungBrowser/i.test(ua)) {
    name = 'samsung'
    version = extractVersion(ua, /SamsungBrowser\/(\d+[\d.]*)/)
  } else if (/Edg\//i.test(ua)) {
    name = 'edge'
    version = extractVersion(ua, /Edg\/(\d+[\d.]*)/)
  } else if (/OPR\//i.test(ua) || /Opera/i.test(ua)) {
    name = 'opera'
    version = extractVersion(ua, /(?:OPR|Opera)\/(\d+[\d.]*)/)
  } else if (/Firefox\//i.test(ua)) {
    name = 'firefox'
    version = extractVersion(ua, /Firefox\/(\d+[\d.]*)/)
  } else if (/Safari\//i.test(ua) && !/Chrome/i.test(ua)) {
    name = 'safari'
    version = extractVersion(ua, /Version\/(\d+[\d.]*)/)
  } else if (/Chrome\//i.test(ua)) {
    name = 'chrome'
    version = extractVersion(ua, /Chrome\/(\d+[\d.]*)/)
  }

  return {
    name,
    version,
    os: detectOS(),
    isChromiumBased: isChromiumBased(),
    isMobile: detectIsMobile()
  }
}

/**
 * Detects the operating system from User-Agent and platform information.
 */
function detectOS (): BrowserInfo['os'] {
  const ua = navigator.userAgent
  const platform = (navigator as any).userAgentData?.platform || navigator.platform || ''

  if (/iPad|iPhone|iPod/.test(ua) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
    return 'ios'
  }
  if (/Android/i.test(ua)) return 'android'
  if (/Mac/i.test(platform)) return 'macos'
  if (/Win/i.test(platform)) return 'windows'
  if (/Linux/i.test(platform)) return 'linux'
  return 'unknown'
}

function detectIsMobile (): boolean {
  const ua = navigator.userAgent
  return /Mobi|Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua) ||
    (navigator.maxTouchPoints > 1 && /Mac/i.test(navigator.platform))
}

function extractVersion (ua: string, regex: RegExp): string {
  const match = ua.match(regex)
  return match ? match[1] : ''
}

function isChromiumBased (): boolean {
  const ua = navigator.userAgent
  return /Chrome|Chromium/i.test(ua) && !/Firefox/i.test(ua)
}

/**
 * Checks browser support for `RTCRtpSender.createEncodedStreams` (legacy)
 * or `RTCRtpSender.prototype.transform` (spec-compliant). These APIs are
 * required for the `@vonage/media-processor` Insertable Streams pipeline.
 */
function hasInsertableStreams (): boolean {
  try {
    return typeof (window as any).RTCRtpSender !== 'undefined' &&
      (typeof (window as any).RTCRtpSender.prototype.createEncodedStreams === 'function' ||
      typeof (window as any).RTCRtpSender.prototype.transform !== 'undefined')
  } catch {
    return false
  }
}

function hasOffscreenCanvas (): boolean {
  return typeof (window as any).OffscreenCanvas !== 'undefined'
}

function hasWebGL (): boolean {
  try {
    const canvas = document.createElement('canvas')
    return !!(canvas.getContext('webgl2') || canvas.getContext('webgl'))
  } catch {
    return false
  }
}

function hasWebWorkers (): boolean {
  return typeof Worker !== 'undefined'
}

/**
 * Performs a comprehensive capability scan of the current browser and
 * returns a {@link SupportInfo} object with per-feature booleans plus
 * a {@link BrowserInfo} descriptor.
 *
 * Results are **not cached** because features could theoretically change
 * during a session (e.g. GPU driver crash disabling WebGL). Callers
 * that need caching should store the result themselves.
 *
 * @returns Granular support information for every video-processor feature.
 */
export function detectBrowserSupport (): SupportInfo {
  const insertableStreams = hasInsertableStreams()
  const offscreenCanvas = hasOffscreenCanvas()
  const webGL = hasWebGL()
  const wasm = hasWebAssembly()
  const mediaPipe = wasm && offscreenCanvas

  return {
    insertableStreams,
    backgroundBlur: insertableStreams && mediaPipe && webGL,
    virtualBackground: insertableStreams && mediaPipe && webGL,
    videoBackground: insertableStreams && mediaPipe && webGL,
    colorFilters: true,
    colorFiltersPipeline: insertableStreams && offscreenCanvas,
    nsfw: true,
    offscreenCanvas,
    webGL,
    webWorkers: hasWebWorkers(),
    browser: detectBrowser()
  }
}

/**
 * Checks whether WebAssembly is supported, which is required for
 * MediaPipe's WASM runtime used by {@link MediaPipeSegmenter}.
 */
export function hasWebAssembly (): boolean {
  try {
    return typeof WebAssembly === 'object' &&
      typeof WebAssembly.instantiate === 'function'
  } catch {
    return false
  }
}

/**
 * Checks whether the browser likely supports MediaPipe ImageSegmenter.
 * Requires WebAssembly + OffscreenCanvas. GPU delegate additionally
 * requires WebGL.
 */
export function hasMediaPipeSupport (): boolean {
  return hasWebAssembly() && hasOffscreenCanvas()
}

/**
 * Shorthand to check whether background blur can run on this browser.
 * Equivalent to `detectBrowserSupport().backgroundBlur`.
 */
export function isBackgroundBlurSupported (): boolean {
  return detectBrowserSupport().backgroundBlur
}
